import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { get as _get, isNil as _isNil, includes as _includes } from 'lodash';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from 'e1p-portals-wizard-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { WizardSingleErrorComponent } from 'gw-portals-wizard-components-ui';
import { isRequired } from 'e1p-portals-required-validator-js';
import { useTranslator } from '@jutro/locale';
import requiredMetadata from '../PaymentDetailsPage.requiredness';
import metadata from '../PaymentDetailsPage.metadata.json5';
import messages from '../PaymentDetailsPage.messages';

const LOB = 'homeowners_EH';

function ViewPaymentDetailsPage(props) {
    const {
        wizardData: rewriteVM,
        updateWizardData,
        viewOnly
    } = props;

    const [producerCodeOptions, setProducerCodeOptions] = useState([]);
    const [requiredFields, updateRequiredFields] = useState([]);
    const { authHeader, authUserData } = useAuthentication();
    const translator = useTranslator();
    
    const availableValuesForPaymentOptions = useMemo(() => {
        const correctOffering = rewriteVM.lobData.homeowners_EH.offerings.value[0];

        if (correctOffering.paymentPlans !== undefined) {
            return correctOffering.paymentPlans
                .filter((paymentPlans) => paymentPlans.name)
                .reduce(
                    (paymentPlanMap, paymentPlan) => ({
                        ...paymentPlanMap,
                        [paymentPlan.billingId]: paymentPlan
                    }),
                    {}
                );
        }

        return {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(rewriteVM.lobData.homeowners_EH.offerings.value)]);

    const hasSignatureRequiredFormsInCurrentJob = _get(
        rewriteVM, 'lobData.homeowners_EH.hasSignatureRequiredFormsInCurrentJob.value'
    );

    useEffect(() => {
        const policyState = _get(rewriteVM, 'baseData.policyAddress.state.value.code');
        const initialRequiredFields = ['consentToRateAcknowledgement']; // Fields to look up by partner/state

        updateRequiredFields(
            isRequired(initialRequiredFields, requiredMetadata, policyState, 'MSA')
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ctrPremium should be based of selected payment plan premium
    const ctrPremium = availableValuesForPaymentOptions[
        _get(rewriteVM, 'bindData.selectedPaymentPlan.value')
    ]?.total?.amount;

    // Filter the selected offering
    const selectedQuoteOffering = _get(rewriteVM, 'quoteData.offeredQuotes.value').find(
        (quoteOffering) => quoteOffering.selected
    );

    const selectedOffering = _get(rewriteVM, 'lobData.homeowners_EH.offerings.value')
        .find((offering) => offering.branchCode === selectedQuoteOffering.branchCode);

    const ratePlans = _get(selectedOffering, 'ratePlans', []);

    // UseMemo to compute CTR and NCRB plan offering details if rate plan changes
    const { ctrPlanOffering, ncrbPlanOffering } = useMemo(
        () => {
            // CTR and NCRB premium details for consent to rate acknowledgement 
            const ctrOffering = ratePlans?.find((plan) => plan.ratePlanType === 'CTR');

            const ncrbOffering = ratePlans?.find((plan) => plan.ratePlanType === 'NCRB');

            return {
                ctrPlanOffering: ctrOffering,
                ncrbPlanOffering: ncrbOffering
            };
        },
        [ratePlans]
    );

    const getRiskAddress = () => {
        const {addressLine1, addressLine2, city, state, postalCode} = _get(rewriteVM, 'lobData.homeowners_EH.coverables.yourHome.ehlocation.value');

        return `${addressLine1}, ${addressLine2 ? `${addressLine2 }, ` : ''}${city}, ${state} ${postalCode}`;
    };
    
    /**
     * Define property overrides for this Jutro component.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const overrideProps = {
        '@field': {
            showOptional: true,
            labelPosition: 'top',
            readOnly: viewOnly
        },
        paymentDetailsPageLoadingIndicator: {
            loaded: true
        },
        paymentDetailsPageContainer: {
            visible: true
        },
        totalPremiumID: {
            value: !_isNil(
                availableValuesForPaymentOptions[
                    _get(rewriteVM, 'bindData.selectedPaymentPlan.value')
                ]
            )
                ? availableValuesForPaymentOptions[
                    _get(rewriteVM, 'bindData.selectedPaymentPlan.value')
                ].total
                : undefined
        },
        mailingAndBillingAddressComponent: {
            transactionVM: rewriteVM,
            updateWizardData,
            viewOnlyMode: true,
            lob: LOB
        },
        paymentOptionsID: {
            submissionVM: rewriteVM,
            authHeader: {},
            updateWizardData,
            LOB,
            viewOnly: true
        },
        monthlyPaymentScheduleComponent: {
            visible: false
        },
        // IAP-371 show TPI component in only read only Mode
        e1ptpiDisplayTableContainer: {
            transactionVM: rewriteVM,
            authHeader,
            updateWizardData,
            setIsSavingTPI: () => { },
            showErrors: false,
            onValidate: () => { },
            disregardFieldValidationParentPage: () => { },
            viewOnlyMode: true,
            showTitle: false
        },
        addThirdPartyInterestButton: {
            visible: false
        },
        insuredContactInfo: {
            viewOnlyMode: true,
            showPaperless: true,
        },
        paymentusComponent: {
            submissionVM: rewriteVM,
            visible: false
        },
        quoteProposalLinkContainer: {
            disabled: true
        },
        signatureComponent: {
            submissionVM: rewriteVM,
            updateWizardData,
            authHeader,
            LOB,
            onSignatureSuccess: () => {},
            producerCodeOptions,
            setProducerCodeOptions,
            viewOnlyMode: true,
            isSignatureRequired: hasSignatureRequiredFormsInCurrentJob
        },
        deliverDocumentsIndComponent: {
            visible: authUserData.permissions_Ext.includes('docdeliveryind_ext'),
            deliverDocInd: _get(rewriteVM, 'value.shouldDeliverDocuments_Ext', true),
            setDeliverDocInd: () => { },
            viewOnlyMode: true
        },
      	AutoPayOptionToggle: {
            disabled: true
        },
        consentToRateMessage: {
            // if CTR plan exists and if CTR is selected then Consent to rate acknowledgement should be displayed with NCRB and CTR premiums
            visible: _includes(requiredFields, 'consentToRateAcknowledgement') && ctrPlanOffering && ctrPlanOffering.isSelected,
            content: translator(messages.consentToRateAcknowledgementMessage,
                {
                    NCRBPremium: ncrbPlanOffering?.totalPremium,
                    CTRPremium: ctrPremium
                }),
        },
        riskAddress: {
            value: getRiskAddress()
        },
        quoteProposalFailureErrorDiv: {
            visible: false
        },
        paperlessEmailChangedMessageDiv: {
            visible: false
        },
        userNotAuthorisedErrorDiv: {
            visible: false
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            signPolicy: () => { }
        },
        resolveComponentMap: {
            WizardSingleErrorComponent
        }
    };

    const readValue = useCallback(
        (id, path) => readViewModelValue(metadata.pageContent, rewriteVM, id, path, overrideProps),
        [rewriteVM, overrideProps]
    );

    return (
        <WizardPage
            showNext={false}
            shouldLink
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={rewriteVM}
                overrideProps={overrideProps}
                resolveValue={readValue}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

ViewPaymentDetailsPage.propTypes = wizardProps;
export default ViewPaymentDetailsPage;
